import { Typography } from "@mui/material";
import { OLButton } from "OLButton";
import { getBackendErrors, getErrorMessage } from "api/errors";
import { useTiers } from "api/tiers";
import { useAppSelector } from "app/hooks";
import { FormButtons } from "components/FormControls/FormButtons";
import { ReactComponent as Expand04Icon } from "components/Icons/expand-04.svg";
import { LoadingInterceptor } from "components/LoadingInterceptor/LoadingInterceptor";
import { useNotifications } from "contexts/Toast/ToastProvider";
import { Form, Formik } from "formik";
import { useMessage } from "hooks/intl";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { selectDefaultLanguageData } from "reduxState/translations/translationsSlice";
import { TierDetailsType } from "types/tierSets";
import { backendErrors } from "utilities/backendErrors";
import { focusError } from "utilities/focusError";
import { links } from "utilities/links";
import { BasicInformation } from "./BasicInformation/BasicInformation";
import { Conditions } from "./Conditions/Conditions";
import { useStyles } from "./TiersForm.styles";

export const TiersForm = ({
  setTierSetData,
  isEdit,
}: {
  setTierSetData: (tierData: { name: string; id: string }) => void;
  isEdit?: boolean;
}) => {
  const [isExpandedView, setExpandedView] = useState(false);
  const [activeTier, setActiveTier] = useState(0);
  const classes = useStyles();
  const { message } = useMessage();
  const { notify } = useNotifications();
  const { useTierSetData, useTierSetTiers, updateTiersInTierSet } = useTiers();
  const navigate = useNavigate();
  const { id: tierSetId } = useParams<{ id: string }>();
  const { data: tierSetTiers } = useTierSetTiers(tierSetId, {
    filters: [
      {
        condition: "sortOrder",
        query: "_orderBy",
        value: "asc",
      },
    ],
  });

  const { data: tierSetData } = useTierSetData(tierSetId as string);
  const defaultLang = useAppSelector(selectDefaultLanguageData);
  const isLoading = !tierSetData || !tierSetTiers;
  const initialValues =
    tierSetData && tierSetTiers?.items && tierSetTiers.items.length > 0
      ? tierSetTiers.items.map((tier) => {
          return {
            ...tier,
            conditions: tier.conditions.map((condition) => {
              const matchedCondition = tierSetData.conditions.find(
                ({ id }) => id === condition.conditionId,
              );

              return {
                attribute: matchedCondition?.attribute || "",
                value: condition.value,
                walletType: matchedCondition?.walletType,
                eventName: matchedCondition?.eventName,
                conditionId: condition.conditionId,
              };
            }),
          };
        })
      : [
          {
            translations: {
              [defaultLang.code]: {
                name: "Default",
                description: "",
                locale: defaultLang.code,
              },
            },
            rewards: [],
            conditions:
              tierSetData?.conditions.map((condition) => ({
                value: undefined,
                attribute: condition.attribute,
                conditionId: condition.id as string,
                walletType: condition.walletType,
                eventName: condition.eventName,
              })) || [],
            active: true,
            levelId: undefined,
            isDefault: true,
          },
        ];

  useEffect(() => {
    if (tierSetData) {
      setTierSetData({
        name: tierSetData.name,
        id: tierSetData.tierSetId,
      });
    }
  }, [tierSetData, setTierSetData]);
  return (
    <LoadingInterceptor isLoading={isLoading}>
      <Formik
        enableReinitialize
        validateOnChange={false}
        validateOnBlur={false}
        initialStatus={{}}
        initialValues={{ tiers: initialValues }}
        onSubmit={async (values, { setSubmitting, setErrors }) => {
          setSubmitting(true);

          try {
            await updateTiersInTierSet({
              tierSetId: tierSetId,
              tiers: values.tiers.map((tier) => ({
                levelId: tier.levelId,
                translations: tier.translations,
                conditions: tier.conditions.map(({ conditionId, value }) => ({
                  conditionId,
                  value,
                })),
                rewards: tier.rewards.map((reward) => ({
                  name: reward.name,
                  code: reward.code,
                  labels: reward.labels,
                  startAt: reward.startAt,
                  endAt: reward.endAt,
                  value: reward.value,
                  active: reward.active,
                })),
                active: tier.active,
              })),
            });

            navigate(links.tierSet(tierSetId));
          } catch (exception) {
            setSubmitting(false);

            const errors = backendErrors(getBackendErrors(exception));
            if (errors?.[""]) {
              // general exceptional error
              notify(
                Array.isArray(errors[""]) ? errors[""].join() : errors[""],
              );
            } else {
              notify(getErrorMessage(exception));
            }

            setErrors(errors);
            focusError();
          }
        }}
      >
        {({
          handleSubmit,
          isSubmitting,
          status,
          values,
          setFieldValue,
          errors,
        }) => {
          return (
            <Form onSubmit={handleSubmit} className={classes.form}>
              {status?.[""] && (
                <span className={`${classes.error} error-anchor`}>
                  {status[""]}
                </span>
              )}
              <Typography className={classes.sectionHeader} mb={8}>
                {message({ id: "basicInformation" })}
              </Typography>

              <BasicInformation
                activeTier={activeTier}
                setActiveTier={setActiveTier}
                tiers={values.tiers as TierDetailsType[]}
                conditions={tierSetData?.conditions || []}
                setFieldValue={setFieldValue}
              />
              <div className={classes.conditionsHeader}>
                <Typography className={classes.sectionHeader}>
                  {message({ id: "tiersConditions" })}
                </Typography>
                <OLButton
                  onClick={() => setExpandedView(true)}
                  className={classes.expandIcon}
                  startIcon={<Expand04Icon />}
                >
                  {message({ id: "expandMore" })}
                </OLButton>
              </div>
              <Conditions
                activeTier={activeTier}
                conditions={tierSetData?.conditions || []}
                tiers={values.tiers as TierDetailsType[]}
                isExpandedView={isExpandedView}
                setExpandedView={setExpandedView}
                errors={errors?.tiers}
                isEdit={isEdit}
              />

              <FormButtons
                disableSubmit={isSubmitting}
                onCancelClick={() => navigate(links.tierSet(tierSetId))}
                onSubmitClick={() => handleSubmit()}
                {...(!isEdit && { submitMessage: "addTier" })}
              />
            </Form>
          );
        }}
      </Formik>
    </LoadingInterceptor>
  );
};
